import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Bill, Order, BillDianSimulation } from '../models';
import { DatabaseService } from './database.service';
import * as QRCode from 'qrcode';
import { v4 as uuid } from 'uuid';

@Injectable({
  providedIn: 'root'
})
export class BillingService {
  private billsSubject = new BehaviorSubject<Bill[]>([]);
  public bills$: Observable<Bill[]> = this.billsSubject.asObservable();

  constructor(private database: DatabaseService) {
    // Esperar un poco para que IndexedDB esté listo
    setTimeout(() => {
      this.loadData();
    }, 300);
  }

  private loadData(): void {
    this.reloadBillsFromDb();
  }

  /** Recarga facturas desde IndexedDB (útil tras inicializar datos de demo). */
  reloadBillsFromDb(): void {
    this.database.getAllBills().subscribe({
      next: (bills) => {
        const billObjects = bills.map(b => this.deserializeBill(b));
        this.billsSubject.next(billObjects);
      },
      error: (error) => {
        console.error('Error loading bills:', error);
        this.billsSubject.next([]);
      }
    });
  }

  private deserializeBill(data: any): Bill {
    const bill = new Bill(
      data.id,
      data.orderId,
      data.tableId,
      data.subtotal || 0,
      data.tax || 0,
      data.discount || 0
    );
    bill.linkedTableIds = data.linkedTableIds || [];
    bill.orderIds = data.orderIds;
    bill.paymentMethod = data.paymentMethod;
    bill.tip = data.tip;
    bill.notes = data.notes;
    bill.status = data.status || 'DRAFT';
    bill.createdAt = data.createdAt ? new Date(data.createdAt) : new Date();
    bill.paidAt = data.paidAt ? new Date(data.paidAt) : undefined;
    if (data.total) {
      bill.total = data.total;
    }
    if (data.dian) {
      bill.dian = {
        cufe: data.dian.cufe,
        qrBase64: data.dian.qrBase64,
        resolutionNumber: data.dian.resolutionNumber,
        invoicePrefix: data.dian.invoicePrefix,
        invoiceNumber: data.dian.invoiceNumber,
        authorizationDate: data.dian.authorizationDate ? new Date(data.dian.authorizationDate) : new Date()
      };
    }
    return bill;
  }

  private serializeBill(bill: Bill): any {
    const payload: any = {
      id: bill.id,
      orderId: bill.orderId,
      tableId: bill.tableId,
      orderIds: bill.orderIds,
      linkedTableIds: bill.linkedTableIds,
      subtotal: bill.subtotal,
      tax: bill.tax,
      discount: bill.discount,
      total: bill.total,
      paymentMethod: bill.paymentMethod,
      tip: bill.tip,
      notes: bill.notes,
      status: bill.status,
      createdAt: bill.createdAt.toISOString(),
      paidAt: bill.paidAt?.toISOString()
    };
    if (bill.dian) {
      payload.dian = {
        cufe: bill.dian.cufe,
        qrBase64: bill.dian.qrBase64,
        resolutionNumber: bill.dian.resolutionNumber,
        invoicePrefix: bill.dian.invoicePrefix,
        invoiceNumber: bill.dian.invoiceNumber,
        authorizationDate: bill.dian.authorizationDate.toISOString()
      };
    }
    return payload;
  }

  getBills(): Bill[] {
    return this.billsSubject.value;
  }

  getBillById(id: string): Bill | undefined {
    return this.getBills().find(bill => bill.id === id);
  }

  getBillsByOrderId(orderId: string): Bill[] {
    return this.getBills().filter(bill =>
      bill.orderId === orderId || (bill.orderIds && bill.orderIds.includes(orderId))
    );
  }

  /**
   * Crea una factura a partir de los pedidos seleccionados de una mesa (módulo Facturación).
   * Agrega datos DIAN simulados. En producción el backend integraría con la DIAN.
   */
  createBillFromOrders(
    tableId: string,
    orderIds: string[],
    subtotal: number,
    tax: number,
    discount: number,
    notes?: string
  ): Promise<Bill> {
    if (orderIds.length === 0) {
      return Promise.reject(new Error('Se requiere al menos un pedido'));
    }
    const bill = new Bill(
      `bill_${uuid()}`,
      orderIds[0],
      tableId,
      subtotal,
      tax,
      discount
    );
    bill.orderIds = orderIds.slice();
    bill.total = subtotal + tax - discount;
    bill.notes = notes;
    bill.status = 'FINALIZED';
    return this.fillDianSimulation(bill).then(() => {
      this.createBill(bill);
      return bill;
    });
  }

  /**
   * Crea una factura a partir de un solo pedido (compatibilidad).
   * En producción el backend integraría con la DIAN.
   */
  createBillFromOrder(order: Order): Promise<Bill | null> {
    const existing = this.getBillsByOrderId(order.id);
    if (existing.length > 0) {
      return Promise.resolve(existing[0]);
    }
    const subtotal = order.totalAmount ?? order.items.reduce((s, i) => s + i.subtotal, 0);
    const tax = order.tax ?? subtotal * 0.19;
    const discount = order.discount ?? 0;
    const bill = new Bill(
      `bill_${uuid()}`,
      order.id,
      order.tableId,
      subtotal,
      tax,
      discount
    );
    bill.linkedTableIds = order.linkedTableIds ?? [];
    bill.total = subtotal + tax - discount;
    bill.notes = order.notes;
    bill.status = 'FINALIZED';
    return this.fillDianSimulation(bill).then(() => {
      this.createBill(bill);
      return bill;
    });
  }

  private async fillDianSimulation(bill: Bill): Promise<void> {
    const cufe = this.generateCufe();
    const now = new Date();
    const resolutionNumber = '18764000000000';
    const prefix = 'FE';
    const invoiceNumber = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}-${String(Math.floor(Math.random() * 99999)).padStart(5, '0')}`;
    let qrBase64: string | undefined;
    try {
      qrBase64 = await QRCode.toDataURL(cufe, { width: 256, margin: 1 });
    } catch {
      qrBase64 = undefined;
    }
    bill.dian = {
      cufe,
      qrBase64,
      resolutionNumber,
      invoicePrefix: prefix,
      invoiceNumber,
      authorizationDate: now
    };
  }

  private generateCufe(): string {
    const hex = '0123456789abcdef';
    let out = '';
    for (let i = 0; i < 96; i++) {
      out += hex[Math.floor(Math.random() * 16)];
    }
    return out;
  }

  createBill(bill: Bill): Bill {
    const serialized = this.serializeBill(bill);
    this.database.createBill(serialized).subscribe({
      next: () => {
        const bills = [...this.getBills(), bill];
        this.billsSubject.next(bills);
      },
      error: (error) => {
        console.error('Error creating bill:', error);
      }
    });
    return bill;
  }

  updateBill(bill: Bill): Bill {
    const serialized = this.serializeBill(bill);
    this.database.updateBill(serialized).subscribe({
      next: () => {
        const bills = this.getBills();
        const index = bills.findIndex(b => b.id === bill.id);
        if (index !== -1) {
          bills[index] = bill;
          this.billsSubject.next([...bills]);
        }
      },
      error: (error) => {
        console.error('Error updating bill:', error);
      }
    });
    return bill;
  }

  finalizeBill(billId: string): void {
    const bill = this.getBillById(billId);
    if (bill) {
      bill.finalize();
      this.updateBill(bill);
    }
  }

  payBill(billId: string, method: 'CASH' | 'CARD' | 'DIGITAL_WALLET'): void {
    const bill = this.getBillById(billId);
    if (bill) {
      bill.pay(method);
      this.updateBill(bill);
    }
  }

  cancelBill(billId: string): void {
    const bill = this.getBillById(billId);
    if (bill) {
      bill.cancel();
      this.updateBill(bill);
    }
  }

  resetData(): void {
    this.billsSubject.next([]);
    // Los datos se limpian desde DataInitService
  }
}
